<template>
    <ValidationErrors :show="showErrors"/>
    <Head :title="'Overview - ' + fixture.title"/>
    <div class="w-full mb-6">
        <FixtureHeader :fixture="fixture"></FixtureHeader>
        <div class="space-y-6 lg:col-span-9">
            <section aria-labelledby="payment-details-heading">
                <form @submit.prevent="submit">
                    <div class="shadow sm:rounded-md sm:overflow-hidden">
                        <div class="bg-white px-4 p-6 space-y-6">
                            <div class="flex pb-5 border-b border-gray-200 justify-between items-center">
                                <h3 class="text-lg leading-6 font-bold text-gray-900">Overview</h3>
                                <Menu as="div" class="relative inline-block text-left">
                                    <div>
                                    <MenuButton class="inline-flex w-full justify-center gap-x-1.5 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
                                        {{ selected_overview }}
                                        <ChevronDownIcon class="-mr-1 h-5 w-5 text-gray-400" aria-hidden="true" />
                                    </MenuButton>
                                    </div>

                                    <transition enter-active-class="transition ease-out duration-100" enter-from-class="transform opacity-0 scale-95" enter-to-class="transform opacity-100 scale-100" leave-active-class="transition ease-in duration-75" leave-from-class="transform opacity-100 scale-100" leave-to-class="transform opacity-0 scale-95">
                                        <MenuItems class="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                            <div class="py-1">
                                                <MenuItem v-for="(type, index) in overview_types" :key="index">
                                                    <a :href="route('frontend.fixture.overview.index', {fixture, type})" :class="[type == selected_overview ? 'bg-gray-100 text-gray-900' : 'text-gray-700', 'block px-4 py-2 text-sm']">{{ type }}</a>
                                                </MenuItem>
                                            </div>
                                        </MenuItems>
                                    </transition>
                                </Menu>
                            </div>

                            <div class="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                                <div class="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                                    <div class="overflow-hidden sm:rounded-lg">

                                        <div class="rounded-md bg-red-100 p-4 mb-5" v-if="warning_message">
                                            <div class="flex">
                                                <div class="flex-shrink-0">
                                                    <InformationCircleIcon class="h-5 w-5 text-red-400" aria-hidden="true"/>
                                                </div>
                                                <div class="ml-3 space-y-3">
                                                    <p class="text-sm text-black-700">{{ warning_message }}</p>
                                                </div>
                                            </div>
                                        </div>

                                        <div v-for="(statGroup, statGroupKey) in stats" :key="statGroupKey">
                                            <h3 class="text-lg leading-6 font-bold text-gray-900 pb-5" v-if="statGroup.name">{{ statGroup.name }}</h3>

                                            <dl class="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 mb-5">
                                                <div v-for="(stat, statKey) in statGroup.stats" :key="statKey" class="relative bg-white pt-5 px-4 pb-12 sm:pt-6 sm:px-6 shadow border rounded-lg overflow-hidden">
                                                    <dt>
                                                        <div class="absolute bg-pitch-purple rounded-md p-3">
                                                            <CurrencyPoundIcon class="h-6 w-6 text-white" aria-hidden="true" />
                                                        </div>
                                                        <p class="ml-16 text-sm font-medium text-gray-500 truncate">{{ stat.name }}</p>
                                                    </dt>
                                                    <dd class="ml-16 pb-6 sm:pb-7">
                                                        <p class="text-xl font-semibold text-gray-900 mb-2" v-if="stat.values.length" v-for="(value, index) in stat.values">
                                                            {{ value }}
                                                        </p>
                                                        <p class="text-xl font-semibold text-gray-900" v-else>{{ formatCurrency(0, default_currency, true) }}</p>

                                                        <div class="absolute bottom-0 inset-x-0 bg-gray-50 px-4 py-4 sm:px-6" v-if="stat.link">
                                                            <div class="text-sm">
                                                                <Link
                                                                    :href="stat.link"
                                                                    class="font-medium text-pitch-purple hover:text-light-pitch-purple">
                                                                    {{ stat.link_text }}
                                                                </Link>
                                                            </div>
                                                        </div>
                                                    </dd>
                                                </div>
                                            </dl>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="sm:flex sm:items-center">
                                <div class="sm:flex-auto">
                                    <h3 class="text-lg leading-6 font-bold text-gray-900">Quotes</h3>
                                </div>
                            </div>
                            <div class="mt-8 flex flex-col">
                                <div class="overflow-x-scroll">
                                    <div class="shadow overflow-x-scroll border-b border-gray-200 sm:rounded-lg">
                                        <table class="min-w-full divide-y divide-gray-200">
                                            <thead class="bg-gray-50">
                                            <tr>
                                                <th scope="col"
                                                    class="px-3 py-4 text-left text-xs text-gray-500 uppercase tracking-wider border-r">
                                                    Quote
                                                </th>
                                                <th scope="col"
                                                    class="px-3 py-4 text-left text-xs text-gray-500 uppercase tracking-wider border-r">
                                                    Client
                                                </th>
                                                <th scope="col"
                                                    class="px-3 py-4 text-left text-xs text-gray-500 uppercase tracking-wider border-r">
                                                    Status
                                                </th>
                                                <th scope="col"
                                                    class="px-3 py-4 text-left text-xs text-gray-500 uppercase tracking-wider border-r">
                                                    Net Sell
                                                </th>
                                                <th scope="col"
                                                    class="px-3 py-4 text-left text-xs text-gray-500 uppercase tracking-wider border-r">
                                                    Gross Sell
                                                </th>
                                                <th scope="col"
                                                    class="px-3 py-4 text-left text-xs text-gray-500 uppercase tracking-wider border-r">
                                                    Net Buy
                                                </th>
                                                <th scope="col"
                                                    class="px-3 py-4 text-left text-xs text-gray-500 uppercase tracking-wider border-r">
                                                    Gross Buy
                                                </th>
                                                <th scope="col"
                                                    class="px-3 py-4 text-left text-xs text-gray-500 uppercase tracking-wider border-r">
                                                    Management Fees
                                                </th>
                                                <th scope="col"
                                                    class="px-3 py-4 text-left text-xs text-gray-500 uppercase tracking-wider border-r">
                                                    Commission
                                                </th>
                                                <th scope="col"
                                                    class="px-3 py-4 text-left text-xs text-gray-500 uppercase tracking-wider border-r">
                                                    Net Profit
                                                </th>
                                                <th scope="col"
                                                    class="px-3 py-4 text-left text-xs text-gray-500 uppercase tracking-wider border-r">
                                                    Gross Profit
                                                </th>
                                                <th scope="col"
                                                    class="px-3 py-4 text-left text-xs text-gray-500 uppercase tracking-wider">
                                                    Profit %
                                                </th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            <tr v-if="fixture.quotes.length > 0" v-for="(quote, index) in fixture.quotes" :key="index"
                                                :class="{'bg-gray-100': quote.status === 'Not Sent', 'bg-yellow-100' : quote.status === 'Sent to Client', 'bg-green-100' : quote.status === 'Confirmed'}"
                                                class="hover:bg-gray-50 cursor-pointer border">
                                                <td class="px-3 py-4 whitespace-nowrap text-sm text-gray-900 border-r ">
                                                    <Link :href="route('frontend.fixture.quote.show', [fixture.id, quote.id])" class="underline">{{ quote.name }}</Link>
                                                </td>
                                                <td class="px-3 py-4 whitespace-nowrap text-sm text-gray-900 border-r">
                                                    <Link
                                                        :href="route('frontend.organisations.clients.organisation.show', quote.client.id)"
                                                        class="underline">{{ quote.client.name }}
                                                    </Link>
                                                </td>
                                                <td class="px-3 py-4 whitespace-nowrap text-sm text-gray-900 border-r">
                                                    <span v-if="quote.status === 'Not Sent'"
                                                          class="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium shadow-md bg-gray-500 text-white"> {{
                                                            quote.status
                                                        }} </span>
                                                    <span v-if="quote.status === 'Sent to Client'"
                                                          class="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium shadow-md bg-gray-100 text-yellow-800"> {{
                                                            quote.status
                                                        }} </span>
                                                    <span v-if="quote.status === 'Confirmed'"
                                                          class="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium shadow-md bg-gray-100 text-green-800"> {{
                                                            quote.status
                                                        }} </span>
                                                    <span v-if="quote.status === 'On Hold'"
                                                          class="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium shadow-md bg-gray-100 text-orange-500"> {{
                                                            quote.status
                                                        }} </span>
                                                    <span v-if="quote.status === 'Suppressed'"
                                                          class="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium shadow-md bg-gray-100 text-gray-500"> {{
                                                            quote.status
                                                        }} </span>
                                                </td>
                                                <td class="px-3 py-4 whitespace-nowrap text-sm text-gray-900 border-r">
                                                    {{  formatCurrency(quote.subtotals.net_sell_total, quote.sell_currency.code ?? 'GBP' , true) }}
                                                </td>
                                                <td class="px-3 py-4 whitespace-nowrap text-sm text-gray-900 border-r">
                                                    {{  formatCurrency(quote.subtotals.gross_sell_total, quote.sell_currency.code ?? 'GBP' , true) }}
                                                </td>
                                                <td class="px-3 py-4 whitespace-nowrap text-sm text-gray-900 border-r">
                                                    {{
                                                        formatCurrency(quote.subtotals.net_buy_total, quote.buy_currency.code ?? 'GBP' , true)
                                                    }}
                                                </td>
                                                <td class="px-3 py-4 whitespace-nowrap text-sm text-gray-900 border-r">
                                                    {{
                                                        formatCurrency(quote.subtotals.gross_buy_total, quote.buy_currency.code ?? 'GBP' , true)

                                                    }}
                                                </td>
                                                <td class="px-3 py-4 whitespace-nowrap text-sm text-gray-900 border-r">
                                                    {{
                                                        formatCurrency(quote.subtotals.management_fees, quote.sell_currency.code ?? 'GBP' , true)
                                                    }}
                                                </td><td class="px-3 py-4 whitespace-nowrap text-sm text-gray-900 border-r">
                                                    {{
                                                    formatCurrency(quote.subtotals.commission, quote.buy_currency.code ?? 'GBP' , true)

                                                }}
                                                </td>
                                                <td class="px-3 py-4 whitespace-nowrap text-sm text-gray-900 border-r">
                                                    {{
                                                        formatCurrency(quote.subtotals.net_profit_total, quote.sell_currency.code ?? 'GBP', true)
                                                    }}
                                                </td>
                                                <td class="px-3 py-4 whitespace-nowrap text-sm text-gray-900 border-r">
                                                    {{
                                                        formatCurrency(quote.subtotals.gross_profit_total, quote.sell_currency.code ?? 'GBP', true)
                                                    }}
                                                </td>
                                                <td class="px-3 py-4 whitespace-nowrap text-sm text-gray-900 border-r">
                                                    {{
                                                        number_format(parseFloat(quote.subtotals.profit_percentage).toFixed(2))
                                                    }}%
                                                </td>
                                            </tr>
                                            <tr v-else>
                                                <td colspan="10"
                                                    class="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    There are no quotes available.
                                                </td>
                                            </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                            <div class="sm:flex sm:items-center">
                                <div class="sm:flex-auto">
                                    <h3 class="text-lg leading-6 font-bold text-gray-900">Supplier Type Breakdown</h3>
                                    <p class="mt-2 text-sm text-gray-700">A list of all supplier types, with the number
                                        assigned to quotes and their amounts.</p>
                                </div>
                            </div>
                            <div class="mt-8 flex flex-col">
                                <div class="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
                                    <div class="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
                                        <div
                                            class="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                                            <table class="min-w-full divide-y divide-gray-300">
                                                <thead class="bg-gray-50">
                                                <tr class="divide-x divide-gray-200">
                                                    <th scope="col"
                                                        class="py-3.5 pl-4 pr-4 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                                                        Supplier Type
                                                    </th>
                                                    <th scope="col"
                                                        class="px-4 py-3.5 text-left text-sm font-semibold text-gray-900">
                                                        Number of Services Confirmed
                                                    </th>
                                                    <th scope="col"
                                                        class="px-4 py-3.5 text-left text-sm font-semibold text-gray-900">
                                                        Number of Services Quoted
                                                    </th>
                                                    <th scope="col"
                                                        class="px-4 py-3.5 text-left text-sm font-semibold text-gray-900">
                                                        Gross Total
                                                    </th>
                                                </tr>
                                                </thead>
                                                <tbody class="divide-y divide-gray-200 bg-white">
                                                    <tr v-for="type in types" :key="type.name" class="divide-x divide-gray-200">
                                                        <td class="whitespace-nowrap py-4 pl-4 pr-4 text-sm font-medium text-gray-900 sm:pl-6">
                                                            {{ type.name }}
                                                        </td>
                                                        <td class="whitespace-nowrap p-4 text-sm text-gray-500">
                                                            {{ type.number_of_services_confirmed }}
                                                        </td>
                                                        <td class="whitespace-nowrap p-4 text-sm text-gray-500">
                                                            {{ type.number_of_services_unconfirmed }}
                                                        </td>
                                                        <td class="whitespace-nowrap p-4 text-sm text-gray-500 grid grid-cols-1 gap-3">
                                                            <span v-for="(amount, currency) in type.amount">
                                                                {{ amount }} ({{ currency }})
                                                            </span>
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="sm:flex sm:items-center">
                                <div class="sm:flex-auto">
                                    <h3 class="text-lg leading-6 font-bold text-gray-900">Invoice &amp; Outstanding</h3>
                                    <p class="mt-2 text-sm text-gray-700">A comparison of invoices broken down by their
                                        paid status along with the amount remaining.</p>
                                </div>
                            </div>
                            <div class="mt-8 flex">
                                <div class="w-2/5">
                                    <!--                                    <PieChart :chartData="chartConfig"/>-->
                                </div>
                                <div class="mt-8 flex flex-col w-3/5">
                                    <div class="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
                                        <div class="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
                                            <div
                                                class="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                                                <table class="min-w-full divide-y divide-gray-300">
                                                    <thead class="bg-gray-50">
                                                    <tr class="divide-x divide-gray-200">
                                                        <th scope="col"
                                                            class="py-3.5 pl-4 pr-4 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                                                            Invoice Reference
                                                        </th>
                                                        <th scope="col"
                                                            class="px-4 py-3.5 text-left text-sm font-semibold text-gray-900">
                                                            Quote
                                                        </th>
                                                        <th scope="col"
                                                            class="px-4 py-3.5 text-left text-sm font-semibold text-gray-900">
                                                            Amount
                                                        </th>
                                                    </tr>
                                                    </thead>
                                                    <tbody class="divide-y divide-gray-200 bg-white">
                                                    <tr v-for="invoice in invoices" :key="invoice.reference"
                                                        class="divide-x divide-gray-200">
                                                        <td class="whitespace-nowrap py-4 pl-4 pr-4 text-sm font-medium text-gray-900 sm:pl-6">
                                                            {{ invoice.reference }}
                                                        </td>
                                                        <td class="whitespace-nowrap p-4 text-sm text-gray-500">
                                                            {{ invoice.quote.name }}
                                                        </td>
                                                        <td class="whitespace-nowrap p-4 text-sm text-gray-500">
                                                            {{
                                                                formatCurrency(invoice.amount, invoice.quote.sell_currency.code, true)
                                                            }}
                                                        </td>
                                                    </tr>
                                                    <tr class="divide-x divide-gray-200 bg-gray-200 font-bold">
                                                        <td class="whitespace-nowrap p-4 text-sm text-gray-500"></td>
                                                        <td class="whitespace-nowrap p-4 text-sm text-gray-500 text-right">
                                                            Outstanding:
                                                        </td>
                                                        <td class="whitespace-nowrap p-4 text-sm text-gray-500 grid grid-cols-1">
                                                            <span v-for="(invoice_stat, index) in invoice_stats"> 
                                                                {{ invoice_stat.uninvoiced_amount }}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                &nbsp;
                            </div>

                            <div class="mt-8 grid grid-cols-5" v-for="(actual, index) in actuals">
                                <div class="sm:flex sm:items-center col-span-5 mb-8">
                                    <div class="sm:flex-auto">
                                        <h3 class="text-lg leading-6 font-bold text-gray-900">Actuals - {{ index }}</h3>
                                        <p class="mt-2 text-sm text-gray-700">An overview of the actuals, including
                                            commission, management fees and expenses.</p>
                                    </div>
                                </div>
                                <div class="col-span-2">
                                    <PieChart :chartData="actualsChartConfig[index]"/>
                                </div>
                                <div class="mt-8 flex flex-col col-span-3">
                                    <div class="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
                                        <div class="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
                                            <div
                                                class="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                                                <table class="min-w-full divide-y divide-gray-300">
                                                    <thead class="bg-gray-50">
                                                    <tr class="divide-x divide-gray-200">
                                                        <th scope="col"
                                                            class="py-3.5 pl-4 pr-4 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                                                            Item
                                                        </th>
                                                        <th scope="col"
                                                            class="px-4 py-3.5 text-left text-sm font-semibold text-gray-900">
                                                            Amount
                                                        </th>
                                                    </tr>
                                                    </thead>
                                                    <tbody class="divide-y divide-gray-200 bg-white">
                                                    <tr v-for="data in actual" class="divide-x divide-gray-200">
                                                        <td class="whitespace-nowrap py-4 pl-4 pr-4 text-sm font-medium text-gray-900 sm:pl-6">
                                                            {{ data.item }}
                                                        </td>
                                                        <td class="whitespace-nowrap p-4 text-sm text-gray-500">
                                                            {{ data.amount }}
                                                        </td>
                                                    </tr>

                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                &nbsp;
                            </div>

                            <div class="mt-8 grid grid-cols-5" v-for="(supplier_invoice_total, currency) in supplier_invoice_totals">
                                <div class="sm:flex sm:items-center col-span-5 mb-8">
                                    <div class="sm:flex-auto">
                                        <h3 class="text-lg leading-6 font-bold text-gray-900">Supplier Invoice
                                            Breakdown - {{ currency }}</h3>
                                        <p class="mt-2 text-sm text-gray-700">An overview of the supplier invoices,
                                            including total paid, outstanding and invoiced.</p>
                                    </div>
                                </div>
                                <div class="col-span-2">
                                    <PieChart :chartData="supplierInvoiceConfig[currency]"/>
                                </div>
                                <div class="mt-8 col-span-3">
                                    <div class="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
                                        <div class="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
                                            <div
                                                class="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                                                <table class="min-w-full divide-y divide-gray-300">
                                                    <thead class="bg-gray-50">
                                                    <tr class="divide-x divide-gray-200">
                                                        <th scope="col"
                                                            class="py-3.5 pl-4 pr-4 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                                                            Item
                                                        </th>
                                                        <th scope="col"
                                                            class="px-4 py-3.5 text-left text-sm font-semibold text-gray-900">
                                                            Amount
                                                        </th>
                                                    </tr>
                                                    </thead>
                                                    <tbody class="divide-y divide-gray-200 bg-white">
                                                    <tr v-for="data in supplier_invoice_total"
                                                        class="divide-x divide-gray-200">
                                                        <td class="whitespace-nowrap py-4 pl-4 pr-4 text-sm font-medium text-gray-900 sm:pl-6">
                                                            {{ data.item }}
                                                        </td>
                                                        <td class="whitespace-nowrap p-4 text-sm text-gray-500">
                                                            {{ data.amount }}
                                                        </td>
                                                    </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                &nbsp;
                            </div>
                            <div class="sm:flex sm:items-center">
                                <div class="sm:flex-auto">
                                    <h3 class="text-lg leading-6 font-bold text-gray-900">Expenses Breakdown</h3>
                                    <p class="mt-2 text-sm text-gray-700">A list of expenses</p>
                                </div>
                            </div>
                            <div class="mt-8 flex flex-col">
                                <div class="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
                                    <div class="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
                                        <div
                                            class="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                                            <table class="min-w-full divide-y divide-gray-300">
                                                <thead class="bg-gray-50">
                                                <tr class="divide-x divide-gray-200">
                                                    <th scope="col"
                                                        class="py-3.5 pl-4 pr-4 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                                                        Expense
                                                    </th>
                                                    <th scope="col"
                                                        class="px-4 py-3.5 text-left text-sm font-semibold text-gray-900">
                                                        Expense Type
                                                    </th>
                                                    <th scope="col"
                                                        class="px-4 py-3.5 text-left text-sm font-semibold text-gray-900">
                                                        Amount
                                                    </th>
                                                </tr>
                                                </thead>
                                                <tbody class="divide-y divide-gray-200 bg-white">
                                                <tr v-for="expense in expenses" :key="expense.id"
                                                    class="divide-x divide-gray-200">
                                                    <td class="whitespace-nowrap py-4 pl-4 pr-4 text-sm font-medium text-gray-900 sm:pl-6">
                                                        {{ expense.transaction_type.name }}
                                                    </td>
                                                    <td class="whitespace-nowrap p-4 text-sm text-gray-500">
                                                        {{ expense.expense_type.name }}
                                                    </td>
                                                    <td class="whitespace-nowrap p-4 text-sm text-gray-500">
                                                        {{ formatCurrency(expense.gbp_amount, fixture.primary_trading_details?.currency?.code ?? default_currency, true) }}
                                                    </td>
                                                </tr>
                                                <tr class="divide-x divide-gray-200 bg-gray-200 font-bold">
                                                    <td class="whitespace-nowrap p-4 text-sm text-gray-500"></td>
                                                    <td class="whitespace-nowrap p-4 text-sm text-gray-500"></td>
                                                    <td class="whitespace-nowrap p-4 text-sm text-gray-500">Total:
                                                        {{ this.fixture.primary_trading_details.currency.symbol + expenses_total }}
                                                    </td>
                                                </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="sm:flex sm:items-center">
                                <div class="sm:flex-auto">
                                    <h3 class="text-lg leading-6 font-bold text-gray-900">Commission Breakdown</h3>
                                    <p class="mt-2 text-sm text-gray-700">A list of commission</p>
                                </div>
                            </div>
                            <div class="mt-8 flex flex-col">
                                <div class="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
                                    <div class="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
                                        <div
                                            class="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                                            <table class="min-w-full divide-y divide-gray-300">
                                                <thead class="bg-gray-50">
                                                <tr class="divide-x divide-gray-200">
                                                    <th scope="col"
                                                        class="py-3.5 pl-4 pr-4 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                                                        Organisation
                                                    </th>
                                                    <th scope="col"
                                                        class="px-4 py-3.5 text-left text-sm font-semibold text-gray-900">
                                                        Reference
                                                    </th>
                                                    <th scope="col"
                                                        class="px-4 py-3.5 text-left text-sm font-semibold text-gray-900">
                                                        Amount
                                                    </th>
                                                    <th scope="col"
                                                        class="px-4 py-3.5 text-left text-sm font-semibold text-gray-900">
                                                        Status
                                                    </th>
                                                </tr>
                                                </thead>
                                                <tbody class="divide-y divide-gray-200 bg-white">
                                                <tr v-for="commission in commissions" :key="commission.id"
                                                    class="divide-x divide-gray-200">
                                                    <td class="whitespace-nowrap py-4 pl-4 pr-4 text-sm font-medium text-gray-900 sm:pl-6">
                                                        {{ commission.organisation.name }}
                                                    </td>
                                                    <td class="whitespace-nowrap p-4 text-sm text-gray-500">
                                                        {{ commission.reference }}
                                                    </td>
                                                    <td class="whitespace-nowrap p-4 text-sm text-gray-500">
                    <span v-if="commission.status === 'Draft'"
                          class="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium shadow-md bg-gray-500 text-white"> {{
                            commission.status
                        }} </span>
                                                        <span v-if="commission.status === 'Sent'"
                                                              class="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium shadow-md bg-gray-500 text-white"> {{
                                                                commission.status
                                                            }} </span>
                                                        <span v-if="commission.status === 'Cancelled'"
                                                              class="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium shadow-md bg-red-500 text-white"> {{
                                                                commission.status
                                                            }} </span>
                                                        <span v-if="commission.status === 'Paid'"
                                                              class="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium shadow-md bg-green-100 text-green-800"> {{
                                                                commission.status
                                                            }} </span>

                                                    </td>
                                                    <td class="whitespace-nowrap p-4 text-sm text-gray-500">
                                                        {{
                                                            formatCurrency(commission.amount, commission.currency, true)
                                                        }}
                                                    </td>

                                                </tr>
                                                <tr class="divide-x divide-gray-200 bg-gray-200 font-bold">
                                                    <td class="whitespace-nowrap p-4 text-sm text-gray-500"></td>
                                                    <td class="whitespace-nowrap p-4 text-sm text-gray-500"></td>
                                                    <td class="whitespace-nowrap p-4 text-sm text-gray-500"></td>
                                                    <td class="whitespace-nowrap p-4 text-sm text-gray-500">Total:
                                                        <span v-for="(total, currency) in commission_totals">
                                                            {{ total }}
                                                        </span>
                                                    </td>
                                                </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </form>
            </section>
        </div>
    </div>
</template>


<script>
import Authenticated from "@/Layouts/Authenticated";
import {Link, Head, usePage} from "@inertiajs/vue3";
import {ArrowSmDownIcon, ArrowSmUpIcon, ChevronDownIcon, CheckIcon, InformationCircleIcon} from '@heroicons/vue/solid'
import {CursorClickIcon, MailOpenIcon, UsersIcon, CurrencyPoundIcon} from '@heroicons/vue/outline'
import ValidationErrors from "@/Components/ValidationErrors";
import FixtureHeader from "@/Components/FixtureHeader";
import Button from "@/Components/Button";
import {emitter} from "@/eventHub";
import {PieChart} from 'vue-chart-3';
import {Chart, registerables} from "chart.js";
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue'

Chart.register(...registerables);

export default {
    layout: Authenticated,
    components: {
        Button,
        FixtureHeader,
        ValidationErrors,
        Link,
        Head,
        ArrowSmUpIcon,
        ArrowSmDownIcon,
        CurrencyPoundIcon,
        PieChart,
        ChevronDownIcon,
        InformationCircleIcon,
        Menu,
        MenuButton,
        MenuItem,
        MenuItems,
    },
    props: {
        fixture: Object,
        stats: Object,
        types: Object,
        invoices: Object,
        paid: Number,
        unpaid: Number,
        remaining: Number,
        actuals: Object,
        supplier_invoice_totals: Object,
        expenses: Object,
        expenses_total: Number,
        commissions: Object,
        commission_totals: Number,
        overview_types: Object,
        selected_overview: String,
        warning_message: String,
        invoice_stats: Object,
        default_currency: String
    },
    data() {
        return {
            showErrors: false,
            emitter: emitter
        }
    },
    setup() {

        const chartConfig = {
            labels: ['Paid', 'Unpaid', 'Outstanding'],
            datasets: [
                {
                    data: [usePage().props.paid, usePage().props.unpaid, usePage().props.remaining],
                    backgroundColor: ['#16a34a', '#ea580c', '#b91c1c'],
                },
            ],
        };

        let actualsChartConfig = []
        const keys = Object.keys(usePage().props.actuals);
        keys.forEach((key, index) => {
            actualsChartConfig[key] =
                {
                    labels: ['Commission', 'Management Fees', 'Expenses'],
                    datasets:
                        [{
                            data: [
                                usePage().props.actuals[key][0]['amount'],
                                usePage().props.actuals[key][1]['amount'],
                                usePage().props.actuals[key][2]['amount'],
                            ],
                            backgroundColor: ['#16a34a', '#ea580c', '#b91c1c'],

                        }]
                }
        })

        let supplierInvoiceConfig = []
        const supplierkeys = Object.keys(usePage().props.supplier_invoice_totals);
        supplierkeys.forEach((key, index) => {
            supplierInvoiceConfig[key] =
                {
                    labels: ['Total Paid', 'Total Outstanding', 'Total Invoiced'],
                    datasets:
                        [{
                            data: [
                                usePage().props.supplier_invoice_totals[key][0]['amount'],
                                usePage().props.supplier_invoice_totals[key][1]['amount'],
                                usePage().props.supplier_invoice_totals[key][2]['amount'],
                            ],
                            backgroundColor: ['#16a34a', '#ea580c', '#b91c1c'],

                        }]
                }
        })

        console.log(supplierInvoiceConfig)

        return {
            chartConfig,
            actualsChartConfig,
            supplierInvoiceConfig
        }
    },
    methods: {
        number_format(nStr) {
            nStr += '';
            let x = nStr.split('.');
            let x1 = x[0];
            let x2 = x.length > 1 ? '.' + x[1] : '';
            let rgx = /(\d+)(\d{3})/;
            while (rgx.test(x1)) {
                x1 = x1.replace(rgx, '$1' + ',' + '$2');
            }
            return x1 + x2;
        },
    }
}
</script>
